import { AxiosError } from 'axios';

import type { Device } from '@/interfaces/device';
import type { ToastMessage } from '@/interfaces/error';
import type { Machine } from '@/interfaces/machine';
import type { User } from '@/interfaces/user';

import { DEPRECATED_NODE_TYPES, NodeType } from '@/interfaces/flow';

export function defaultAxiosErrorHandler(
  error: unknown,
  fallbackErrorMessage: string,
): ToastMessage {
  if (error instanceof AxiosError) {
    if (Array.isArray(error.response?.data.detail)) {
      const text = error.response?.data.detail
        .map((d: { field: string; message: string }) => `${d.field}: ${d.message}`)
        .join('\n');
      return { type: 'danger', text };
    }
    const text = error.response?.data?.detail ?? error.message;
    return { type: 'danger', text };
  } else {
    return { type: 'danger', text: fallbackErrorMessage };
  }
}

export const EXPLORER_SESSION_STORAGE_KEY = 'adsExplorerVariables';

export const nodeWidth = 225;
export const nodeHeight = 200;

export const downloadFile = (data: string, filename: string): void => {
  const objectURL = URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = objectURL;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectURL);
};

const dateLessThan = (date: Date, minutes: number): boolean => {
  const now = new Date();
  const pastDate = new Date(now.getTime() - minutes * 60000);
  return new Date(date) >= pastDate;
};

export const isMachineOnline = (machine: Machine): boolean => {
  if (!machine.auto_discovered) {
    return true;
  }
  return dateLessThan(machine.last_seen, 5);
};

export const getDeploymentStatus = (device: Device) => {
  if (!device.deployment_status) return { text: 'Not Deployed', class: 'text-secondary' };

  switch (device.deployment_status.status) {
    case 'pending':
      return { text: 'Pending', class: 'text-warning' };
    case 'success':
      return { text: 'Success', class: 'text-success' };
    case 'failed':
      return { text: 'Failed', class: 'text-danger' };
    default:
      return { text: 'Unknown', class: 'text-muted' };
  }
};

export const getNodeDisplay = (
  nodeType: NodeType,
): {
  nodeType: NodeType;
  name: string;
  icon: string;
} => {
  const name = nodeType.replace(/[_:]/g, ' ').toLowerCase();

  const iconMap: Record<NodeType, string> = {
    // Data Source Nodes
    [NodeType.ADS]: 'bi-hdd-network',
    [NodeType.OPC]: 'bi-hdd-network',
    [NodeType.OPC_PULL]: 'bi-hdd-network',
    [NodeType.OPC_EVENT]: 'bi-calendar-event',
    [NodeType.MODBUS]: 'bi-plug',
    [NodeType.MQTT_INPUT]: 'bi-broadcast-pin',
    [NodeType.MQTT_OUTPUT]: 'bi-broadcast-pin',
    [NodeType.FAKE]: 'bi-activity',
    [NodeType.DERBY]: 'bi-database-fill-gear',

    // Processing Nodes
    [NodeType.RELATIVE_CALCULATION]: 'bi-calculator-fill',
    [NodeType.ON_CHANGE]: 'bi-arrow-clockwise',
    [NodeType.OEE]: 'bi-speedometer',
    [NodeType.STATION_TIMES]: 'bi-stopwatch-fill',
    [NodeType.DATAMODLER]: 'bi-diagram-3-fill',
    [NodeType.NODE_RED]: 'bi-diagram-2',
    [NodeType.MERGE]: 'bi-diagram-3-fill',
    [NodeType.FILTER]: 'bi-funnel-fill',

    // Upload Nodes
    [NodeType.IOTHUB]: 'bi-cloud-upload-fill',
    [NodeType.FTP_SAM]: 'bi-file-earmark-arrow-up-fill',
  };

  const icon = iconMap[nodeType] || 'bi-robot';
  return { nodeType, name, icon };
};

export const checkIfProduction = () => {
  return import.meta.env.VITE_STAGE === 'production';
};

export type StagingColor = {
  sidebarColor: string;
  chipColorClass: string;
  chipText: string;
};

export const getStageColor = (): StagingColor => {
  const prod = checkIfProduction();
  return {
    sidebarColor: prod ? '#1E2B51' : '#511E2B',
    chipColorClass: prod ? 'text-bg-success' : 'text-bg-danger',
    chipText: prod ? 'Production' : 'Development',
  };
};

export const escapeCsvValue = (value: string): string => {
  if (value == null) return '';

  // escape internal quotes
  const escaped = value.replace(/"/g, '""');

  // wrap in quotes if needed
  if (/[,"\n]/.test(escaped)) {
    return `"${escaped}"`;
  }
  return escaped;
};

export const minExpiryDate = new Date().setUTCDate(new Date().getUTCDate() + 1);

export const translateCreator = (creator: string, users: User[]): string => {
  const user = users.find((user) => user.client_id === creator);
  return user?.display_name ?? creator;
};

export const getStage = (): 'production' | 'development' | 'local' => {
  const stage = import.meta.env.VITE_STAGE;
  return stage;
};

export const toLocalTime = (ts: string) => {
  return new Date(ts).toLocaleString();
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
};

export const isDeprecatedNodeType = (nodeType: NodeType): boolean => {
  return DEPRECATED_NODE_TYPES.has(nodeType);
};
